import { useRef, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import {
  fileToDataUrl,
  MAX_IMAGE_SIZE_BYTES,
  MAX_IMAGE_SIZE_LABEL,
} from '../api/imageUploadApi';
import type { SelectedImageData } from './imageInsertTypes';

export interface UploadPanelState {
  selectedFile: File | null;
  preview: string | null;
  isUploading: boolean;
  error: string | null;
  tooLarge: boolean;
}

const TOO_LARGE_MESSAGE = `This image exceeds the ${MAX_IMAGE_SIZE_LABEL} file size limit. Try compressing the image or saving it with lower quality settings to reduce the file size, then upload again.`;

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

interface UploadPanelProps {
  state: UploadPanelState;
  onStateChange: (state: UploadPanelState) => void;
  onImageReady: (data: SelectedImageData) => void;
}

export function UploadPanel({ state, onStateChange, onImageReady }: UploadPanelProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const updateState = useCallback(
    (updates: Partial<UploadPanelState>) => {
      const nextState = { ...stateRef.current, ...updates };
      stateRef.current = nextState;
      onStateChange(nextState);
    },
    [onStateChange]
  );

  const acceptFile = useCallback(
    async (file: File) => {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        updateState({
          error: null,
          tooLarge: true,
          selectedFile: file,
          preview: null,
        });
        return;
      }

      updateState({ error: null, tooLarge: false, selectedFile: file, preview: null });

      try {
        const dataUrl = await fileToDataUrl(file);
        updateState({ preview: dataUrl });
      } catch {
        updateState({ error: 'Failed to preview image' });
      }
    },
    [updateState]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await acceptFile(file);
    },
    [acceptFile]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      if (state.isUploading) return;

      const file = e.dataTransfer.files[0];
      if (!file || !file.type.startsWith('image/')) {
        updateState({ error: 'Please drop an image file', tooLarge: false });
        return;
      }

      await acceptFile(file);
    },
    [state.isUploading, updateState, acceptFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleContinue = useCallback(() => {
    if (!state.selectedFile || !state.preview) return;

    onImageReady({
      previewUrl: state.preview,
      file: state.selectedFile,
      needsUpload: true,
      suggestedAlt: '',
      aiGeneratedAlt: false,
      mimeType: state.selectedFile.type,
      sourceType: 'upload',
    });
  }, [state.selectedFile, state.preview, onImageReady]);

  const handleDropZoneClick = useCallback(() => {
    if (!state.isUploading) {
      fileInputRef.current?.click();
    }
  }, [state.isUploading]);

  const handleDropZoneKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !state.isUploading) {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [state.isUploading]);

  const handleDismissError = useCallback(() => {
    updateState({
      selectedFile: null,
      preview: null,
      tooLarge: false,
      error: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [updateState]);

  return (
    <div style={styles.content}>
      <div
        style={{
          ...styles.dropZone,
          ...(state.isUploading ? styles.dropZoneDisabled : {}),
          ...(state.tooLarge ? styles.dropZoneError : {}),
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleDropZoneClick}
        onKeyDown={handleDropZoneKeyDown}
        role="button"
        tabIndex={state.isUploading ? -1 : 0}
        aria-label={
          state.tooLarge
            ? 'Selected image is too large, choose another'
            : state.preview
              ? 'Change selected image'
              : 'Select an image file'
        }
        aria-disabled={state.isUploading}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          style={styles.fileInput}
          aria-label="Upload image file"
          disabled={state.isUploading}
        />
        {state.tooLarge && state.selectedFile ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDismissError();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                }
              }}
              style={styles.errorDismiss}
              aria-label="Dismiss error and clear selection"
            >
              ✕
            </button>
            <div style={styles.errorState} role="alert" aria-live="polite">
              <p style={styles.errorHeadline}>File too large</p>
              <p style={styles.errorFileMeta}>
                {state.selectedFile.name} — {formatFileSize(state.selectedFile.size)}
              </p>
              <p style={styles.errorGuidance}>{TOO_LARGE_MESSAGE}</p>
            </div>
          </>
        ) : state.preview ? (
          <img src={state.preview} alt="Preview of selected image" style={styles.previewImage} />
        ) : (
          <div style={styles.dropZoneText}>
            <span style={styles.dropIcon} aria-hidden="true">
              +
            </span>
            <p style={styles.dropMainText}>Drop an image here or click to browse</p>
            <p style={styles.dropHint}>JPEG, PNG, GIF, WebP (max {MAX_IMAGE_SIZE_LABEL})</p>
          </div>
        )}
      </div>

      {state.error && (
        <p style={styles.error} role="alert" aria-live="polite">
          {state.error}
        </p>
      )}

      {state.preview && state.selectedFile && (
        <button
          type="button"
          style={styles.continueButton}
          onClick={handleContinue}
          aria-label="Continue to add alt text"
        >
          Continue
        </button>
      )}
    </div>
  );
}

export function createInitialUploadState(): UploadPanelState {
  return {
    selectedFile: null,
    preview: null,
    isUploading: false,
    error: null,
    tooLarge: false,
  };
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    content: {
      padding: '20px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      flex: 1,
    },
    dropZone: {
      position: 'relative',
      borderWidth: '2px',
      borderStyle: 'dashed',
      borderColor: tokens.text.subtle,
      borderRadius: '8px',
      padding: '24px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: `border-color ${tokens.ui.transitionDefault}, background-color ${tokens.ui.transitionDefault}`,
      minHeight: '160px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.backgrounds.app,
    },
    dropZoneDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    dropZoneError: {
      borderColor: tokens.ui.errorBorder,
    },
    errorState: {
      color: tokens.ui.errorText,
      textAlign: 'center',
      maxWidth: '420px',
    },
    errorHeadline: {
      fontSize: '14px',
      fontWeight: 600,
      margin: '0 0 4px 0',
    },
    errorFileMeta: {
      fontSize: '13px',
      margin: '0 0 8px 0',
      wordBreak: 'break-all',
    },
    errorGuidance: {
      fontSize: '13px',
      margin: 0,
      lineHeight: 1.4,
    },
    errorDismiss: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '28px',
      height: '28px',
      padding: 0,
      border: 'none',
      borderRadius: '4px',
      backgroundColor: 'transparent',
      color: tokens.ui.errorText,
      fontSize: '16px',
      lineHeight: 1,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    },
    dropZoneText: {
      color: tokens.text.muted,
    },
    dropIcon: {
      fontSize: '32px',
      display: 'block',
      marginBottom: '8px',
      color: tokens.text.subtle,
    },
    dropMainText: {
      fontSize: '14px',
      margin: '0 0 4px 0',
    },
    dropHint: {
      fontSize: '12px',
      color: tokens.text.subtle,
      margin: 0,
    },
    fileInput: {
      display: 'none',
    },
    previewImage: {
      maxWidth: '100%',
      maxHeight: '200px',
      objectFit: 'contain',
      borderRadius: '4px',
    },
    error: {
      color: tokens.ui.errorText,
      fontSize: '14px',
      margin: 0,
    },
    continueButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '6px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
      alignSelf: 'flex-end',
    },
  };
}
